import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../Firebaseconfig';
import { FaStar, FaTimes } from 'react-icons/fa';

export default function RatingModal({ order, currentUser, onClose }) {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alreadyReviewed, setAlreadyReviewed] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);

  useEffect(() => {
    // Check if the user has already reviewed this specific order
    const checkExistingReview = async () => {
      try {
        const q = query(collection(db, 'ratings'), where('orderId', '==', order.id));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          setAlreadyReviewed(true);
        }
      } catch (err) {
        console.error("Failed to check existing review:", err);
      } finally {
        setCheckingStatus(false);
      }
    };
    checkExistingReview();
  }, [order.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0 || !currentUser) return;
    setIsSubmitting(true);

    try {
      const ratingData = {
        orderId: order.id,
        customerId: currentUser.uid,
        customerName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown',
        companyId: order.companyId,
        companyName: order.companyName || 'Unknown Company',
        stars: rating,
        comment: comment.trim(),
        createdAt: serverTimestamp(),
      };

      await addDoc(collection(db, 'ratings'), ratingData);
      onClose(); // Automatically close after success
    } catch (err) {
      console.error("Failed to submit rating:", err);
      alert("Failed to submit review. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="rm-overlay">
        <div className="rm-modal">
          <div className="rm-loading">{t('orders.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rm-overlay" onClick={onClose}>
      <div className="rm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rm-header">
          <h3>{t('rating.modalTitle')}</h3>
          <button className="rm-close-btn" onClick={onClose}><FaTimes /></button>
        </div>

        {alreadyReviewed ? (
          <div className="rm-body rm-already-reviewed">
            <p>{t('rating.alreadyReviewed')}</p>
            <button className="rm-btn-secondary" onClick={onClose}>{t('rating.close')}</button>
          </div>
        ) : (
          <form className="rm-body" onSubmit={handleSubmit}>
            <p className="rm-subtitle">{t('rating.rateExperience')}</p>
            
            <div className="rm-stars-container">
              {[1, 2, 3, 4, 5].map((star) => (
                <FaStar
                  key={star}
                  className={`rm-star ${star <= (hoverRating || rating) ? 'active' : ''}`}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(star)}
                />
              ))}
            </div>

            <textarea
              className="rm-textarea"
              placeholder={t('rating.commentPlaceholder')}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={300}
              rows={4}
            />

            <div className="rm-footer">
              <button 
                type="button" 
                className="rm-btn-secondary" 
                onClick={onClose}
              >
                {t('rating.close')}
              </button>
              <button 
                type="submit" 
                className="rm-btn-primary" 
                disabled={rating === 0 || isSubmitting}
              >
                {isSubmitting ? t('rating.submitting') : t('rating.submit')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
